import { ConnectedPlumaProvider } from '@/components/connected-pluma-provider';
import React, { Suspense } from 'react';
const DevHelper = React.lazy(() => import('./components/dev-helper').then((m) => ({ default: m.DevHelper })));
import { ActiveAppContextProvider } from '@/context/active-bundle';
import { BackgroundAppBundleContextProvider } from '@/context/background-app-bundle';
import { ThemeContextProvider } from '@/context/theme';
import { getSampleAccounts } from '@/features/workspaces';
import { useInjectImportShim } from '@/hooks/use-inject-import-shim';
import { useLoadShims } from '@/hooks/use-load-shims';
import { RouterProvider } from '@/services/router';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { isHydraRoute } from '@/services/router/generate-path';

import { authStore } from '@/services/auth/store';
import type { AppBundle } from '@/utils/app-bundles';
import { isEmptyString } from '@/utils/string';
import tracking from '@/utils/tracking-scripts';
import { activeRouteStore, setActiveRoute, subscribeToHydraNavigate } from '@customerio/hydra-nav-state';
import { Box, PageLoader } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createAppQueryCache } from '@/services/query-error-handler';
import { LazyMotion } from 'motion/react';
import type { ReactNode } from 'react';
import { use, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, UNSAFE_DataRouterContext, useLocation } from 'react-router-dom';

import { NavigationGuardProvider } from '@/services/navigation-guard';
import { preloadJourneysAppBundle } from '@/pages/journeys';
import { axiosServicesApi } from '@/services/api/deprecated';
import { crossAppCache } from '@/utils/cross-app-cache';
import { HelmetProvider } from 'react-helmet-async';
import * as styles from './index.css';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  queryCache: createAppQueryCache(),
});

const prefetchSampleAccounts = async () => {
  await queryClient.prefetchQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });
};

export default function Root() {
  const loaded = useLoadShims();

  useMountEffect(() => {
    prefetchSampleAccounts();
  });

  useEffect(() => {
    if (loaded) {
      preloadJourneysAppBundle();
    }
  }, [loaded]);

  performance.mark('hydra:root:render');

  return (
    <HelmetProvider>
      <LazyMotion features={() => import('./utils/motion-features').then((mod) => mod.domMax)}>
        <QueryClientProvider client={queryClient}>
          <ActiveAppContextProvider>
            <BackgroundAppBundleContextProvider>
              <RouterProvider>
                <ThemeContextProvider>
                  <ConnectedPlumaProvider>
                    <NavigationGuardProvider>{loaded ? <App /> : null}</NavigationGuardProvider>
                  </ConnectedPlumaProvider>
                </ThemeContextProvider>
              </RouterProvider>
            </BackgroundAppBundleContextProvider>
          </ActiveAppContextProvider>
        </QueryClientProvider>
      </LazyMotion>
    </HelmetProvider>
  );
}

type AppContainerProps = { children?: ReactNode };

function AppContainer({ children }: AppContainerProps) {
  return (
    <div className={styles.appRoot}>
      <Box id="in-app-target_root-announcement" flexShrink="0" />
      {children}

      <Suspense>
        <DevHelper />
      </Suspense>
    </div>
  );
}

function App() {
  const dataRouterContext = use(UNSAFE_DataRouterContext);
  const location = useLocation();

  const safeNavigate = useSafeNavigate();

  const isLoaded = useInjectImportShim();
  const activeRoute = useStore(activeRouteStore);

  /**
   * We use this code here to track users on injected
   * scripts as soon as they are authenticated
   * (as opposed to waiting for a user
   * to navigate to an authenticated URL)
   */
  const auth = useStore(authStore);
  const hasInjectedScripts = useRef<boolean>(false);
  const { account, user, workspaces } = auth;

  useEffect(() => {
    if (account !== null && user !== null && !hasInjectedScripts.current) {
      // We only ever want to inject once.
      hasInjectedScripts.current = true;
      tracking.load({ authenticated: true, account, user });
    }
  }, [account, user]);

  const hasAttemptedWorkspacePreload = useRef<boolean>(false);

  // Preload workspace data for use on Journeys, aimed at
  // improving app initialization time.
  useEffect(
    function preloadWorkspaceData() {
      if (account != null && user != null && workspaces != null && !hasAttemptedWorkspacePreload.current) {
        hasAttemptedWorkspacePreload.current = true;

        const lastCioWorkspaceId = user.last_cio_workspace_id;

        axiosServicesApi.get(`/environments/${lastCioWorkspaceId}`).then((response) => {
          // Sanity check to avoid caching invalid data
          if (response.data?.environment?.id != null) {
            crossAppCache.setValue({
              key: `/environments/${lastCioWorkspaceId}`,
              value: response.data,
              ttl: 10000,
              cacheOnlyOnce: true,
            });
          }
        });
      }
    },
    [user, workspaces, account],
  );

  // Set activeRoute for Hydra-only URLs when location changes
  // This handles cases where no child app is loaded to call setActiveRoute
  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;

    // Set the initial activeRoute state if it's empty
    if (activeRoute === '' && activeRoute !== currentPath) {
      setActiveRoute(currentPath);
      return;
    }

    const isCurrentPathHydraOnly = isHydraRoute(currentPath);
    const isActiveRouteHydraOnly = activeRoute === '' || isHydraRoute(activeRoute);
    if (isCurrentPathHydraOnly && isActiveRouteHydraOnly && activeRoute !== currentPath) {
      setActiveRoute(currentPath);
    }
  }, [location.pathname, location.search, activeRoute]);

  // uses useLayoutEffect, rather than useEffect, to ensure this runs before
  // other effects have a chance to run, or components have a chance to render.
  useLayoutEffect(() => {
    const router = dataRouterContext?.router;

    if (!isEmptyString(activeRoute) && router != null) {
      // Force React Router's internal location state to update without
      // triggering a navigation (that would add browser history entries, which
      // we're trying to avoid).
      // This is so that embedded app navigation gets synchronized with Hydra
      // UI's location state whenever a route changes.
      const [pathname, search] = activeRoute.split('?');
      router.state.location.pathname = pathname;
      router.state.location.search = search != null ? `?${search}` : '';
      router.revalidate();
    }
  }, [activeRoute]);

  useEffect(() => {
    return subscribeToHydraNavigate((event) => {
      safeNavigate(event.detail.url, {
        shouldFadeOut: event.detail.options?.shouldFadeOut,
        shouldUnrenderOutlet: event.detail.options?.shouldUnrenderOutlet,
        shouldReplaceHistoryEntry: event.detail.options?.shouldReplaceHistoryEntry,
        targetApp: event.detail.options?.targetApp as AppBundle | undefined,
      });
    });
  }, [safeNavigate]);

  return <AppContainer>{isLoaded ? <Outlet /> : <PageLoader />}</AppContainer>;
}
